<!-- 常見問題：/FAQ/:type? -->
<template>
  <div class="layout_wrapper faq_wrapper">
    <div class="container_wrapper">
      <h3 class="page_main_title">
        {{ categoryTitle }}
      </h3>

      <TabMenu
        :model="categoryItems"
        :active="0"
      />

      <Accordion v-model:activeIndex="activeIdx">
        <AccordionTab
          v-for="(item, idx) in currentContent"
          :key="idx"
        >
          <template #header>
            <div
              :id="`accordion-header-${idx}`"
              class="accordion-header"
              @click="doAction(idx, item.title)"
            >
              <span class="accordion-header-text">{{ item.title }}</span>

              <span
                :id="`icon_${idx}`"
                :class="activeIdx !== idx ? 'add_icon' : 'minus_icon'"
              />
            </div>
          </template>

          <p
            v-for="(itemText, number) in item.content.split('\n')"
            :key="number"
            class="item_content"
            @click.prevent="handlePxplusLink"
            v-html="itemText"
          />
        </AccordionTab>
      </Accordion>
    </div>

    <component :is="dialogName" />
  </div>
</template>

<script>
import { ref, reactive, nextTick, onMounted } from 'vue';
import { onBeforeRouteUpdate } from 'vue-router';
import { getFAQList } from '@/libs/API/apis';
import { AppDialog } from '@/components/Modal/';
import { setGaEvent, handlePxplusLink } from '@/libs/global';
import useCommon from '@/libs/globalComposition';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import TabMenu from 'primevue/tabmenu';

export default {
  name: 'FAQList',
  components: {
    TabMenu,
    Accordion,
    AccordionTab,
    AppDialog
  },
  setup () {
    const { currentRoute, router, dialogName } = useCommon();
    const categoryItems = ref([]);
    const faqFileContent = reactive({ data: {} });
    const currentContent = ref([]);
    const categoryTitle = ref('');
    const activeIdx = ref(0);

    const doAction = (idx, title) => {
      if (activeIdx.value !== idx) setGaEvent('qna', { [currentRoute.params.type]: title });
    };

    const getTabAction = (path) => {
      const [, , type, count] = path.split('/');

      categoryTitle.value = decodeURI(type);
      currentContent.value = faqFileContent.data[categoryTitle.value];

      if (!currentContent.value) {
        router.push('/FAQ/註冊');
        return;
      }

      if (count && currentContent.value.length >= count) {
        activeIdx.value = count - 1;

        nextTick(() => {
          const currentItem = document.getElementById(`accordion-header-${activeIdx.value}`);
          currentItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      const tabMenuIdx = Object.keys(faqFileContent.data).indexOf(categoryTitle.value);
      categoryItems.value[tabMenuIdx].to = path; // 頁面預帶項目編號會造成 tabMenu 無法顯示 active，故需動態調整 to 參數

      setGaEvent('qna', { category: categoryTitle.value });

      nextTick(() => {
        if (!count && activeIdx.value !== null && currentContent.value.length > activeIdx.value) {
          document.getElementsByClassName('p-accordion-header-link')[activeIdx.value].click();
        }
      });
    };

    onMounted(() => {
      onBeforeRouteUpdate((to) => {
        getTabAction(to.path);
      });

      getFAQList().then((res) => {
        Object.keys(res).forEach((categoryName, idx) => {
          categoryItems.value.push({ label: categoryName, to: `/FAQ/${encodeURI(categoryName)}` });
        });

        faqFileContent.data = res;
        getTabAction(currentRoute.path);
      });
    });

    return {
      dialogName,
      categoryItems,
      faqFileContent,
      currentContent,
      categoryTitle,
      activeIdx,
      doAction,
      handlePxplusLink
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/scss/common/common.scss';
@import '@/scss/common/tabs.scss';
@import '@/scss/common/layout.scss';

::v-deep(.p-accordion) {
  background: $white;

  .p-accordion-tab {
    margin: 30px 0px;

    &:last-child {
      margin-bottom: 0;
    }

    .item_content {
      @include font-size-18;

      color: $mono_80;
      margin: 5px 0px;
      min-height: 26px;

      &:first-child {
        margin-top: 0px;
      }

      &:last-child {
        margin-bottom: 0px;
      }

      .item_url {
        color: $green;
        cursor: pointer;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    &.p-accordion-tab-active {
      background: transparent;
    }

    .p-accordion-toggle-icon {
      display: none;
    }

    .p-accordion-header {
      background: transparent;

      &:not(.p-highlight) {
        .p-accordion-header-link {
          border-radius: 0px;
        }
      }

      &.p-highlight {
        background: transparent;
      }

      .p-accordion-header-link {
        background: transparent;
        border: none;
        border-bottom: 1px solid $mono_30;
        border-radius: none;
        color: $black;
        font-size: 24px;
        line-height: 35px;
        padding: 0px 0px 10px;
        position: relative;
        -webkit-tap-highlight-color: transparent;

        &:hover {
          background: transparent;
          border-bottom: 1px solid $mono_30;
          color: $black;
        }

        &:focus {
          box-shadow: none;
        }

        .accordion-header {
          width: 100%;
        }
        .accordion-header-text {
          display: block;
          font-size: 24px;
          line-height: 35px;
          margin-right: 33px;
        }

        .add_icon {
          background: url('../assets/imgs/add.svg') center center no-repeat;
          background-size: contain;
          display: inline-block;
          height: 33px;
          position: absolute;
          right: 0px;
          top: calc(50% - 5px);
          transform: translateY(-50%);
          width: 33px;
        }

        .minus_icon {
          background: url('../assets/imgs/minus.svg') center center no-repeat;
          background-size: contain;
          display: inline-block;
          height: 33px;
          position: absolute;
          right: 0px;
          top: calc(50% - 5px);
          transform: translateY(-50%);
          width: 33px;
        }
      }
    }

    .p-accordion-content {
      border: none;
      padding: 19px 0px 0px;
      text-align: left;

      .content {
        @include font-size-18;
      }
    }
  }
}

@include RWD_414 {
  ::v-deep(.p-accordion) {
    .p-accordion-tab {
      &:first-child {
        margin-top: 10px
      }

      .item_content {
          @include font-size-16;

          line-height: 26px;
      }
    }
  }
}
</style>
